import SparkMD5 from 'spark-md5';
import { Dispatch } from 'redux';
import { sum } from 'lodash';
import { api } from '../../../general/apiScenarios';
import { dropLicenseIds, getLicenseIds } from '../../../general/fontLicense/licenseRegistry';
import { isNonRetryableUploadCode, uploadErrorMessage } from '../../../general/fontLicense/model';
import { errorTextFromResponse } from '../../../general/utils/apiErrorText';
import { computeFileMd5 } from '../../../general/utils/computeFileMd5';
import type { AssetLabLogger } from '../app/logs';
import * as actions from './actions';
import { dropFile, getFile } from './fileRegistry';
import { Job, JobStatus } from './model';

const CHUNK_SIZE = 150 * 1024 * 1024; // 150 MiB
const MAX_UPLOAD_RETRIES = 5;
const RETRY_DELAY_MS = 2000;

/**
 * Error that must not be retried — the same class of failure Creative Hub marks with
 * its own UploadError (uploadSingleFile.saga.ts). License/validation errors are
 * deterministic: retrying them just burns 5 attempts × RETRY_DELAY_MS before showing
 * the same reason. A plain Error is not enough here, because the retry loop's own
 * catch would swallow it and try again.
 */
class NonRetryableUploadError extends Error {}

export async function uploadJob(
  dispatch: Dispatch,
  job: Job,
  signal: AbortSignal,
  log?: AssetLabLogger,
): Promise<void> {
  if (signal.aborted) return;

  const file = getFile(job.uuid);
  if (!file) {
    dispatch(
      actions.jobUpdated({
        jobID: job.uuid,
        jobProps: { status: JobStatus.UPLOAD_FAILURE, errorMessage: 'File reference lost' },
      }),
    );
    return;
  }

  dispatch(
    actions.jobUpdated({
      jobID: job.uuid,
      jobProps: { status: JobStatus.UPLOAD_INPROGRESS, uploadProgress: 0 },
    }),
  );

  const uploadStartTime = Date.now();

  // Ids of the FontLicense records created from the license form, keyed by the File
  // reference (bound via bindLicenseIdsToFiles). An archive carries the ids of every
  // font found inside it on the client — the backend does not look into archives.
  const fontLicenseIds = getLicenseIds(file);

  try {
    const createdNodeId =
      file.size <= CHUNK_SIZE
        ? await uploadSinglePart(dispatch, job, file, signal, fontLicenseIds)
        : await uploadMultipart(dispatch, job, file, signal, fontLicenseIds);
    if (signal.aborted) return;
    dropFile(job.uuid);
    dropLicenseIds(file);
    dispatch(
      actions.jobUpdated({
        jobID: job.uuid,
        jobProps: { status: JobStatus.FINISH, uploadProgress: 1, createdNodeId },
      }),
    );
    log?.({
      action: 'Upload',
      sub_action: 'Complete',
      extra: {
        int_1: createdNodeId,
        text_1: file.name,
        int_2: Date.now() - uploadStartTime,
        int_3: file.size,
      },
    });
  } catch (e) {
    if (signal.aborted) return;
    const message = e instanceof Error ? e.message : 'Unknown upload error';
    dispatch(
      actions.jobUpdated({
        jobID: job.uuid,
        jobProps: { status: JobStatus.UPLOAD_FAILURE, errorMessage: message },
      }),
    );
  }
}

async function uploadSinglePart(
  dispatch: Dispatch,
  job: Job,
  file: File,
  signal: AbortSignal,
  fontLicenseIds?: number[],
): Promise<number> {
  const linreg = new TimeWindowedLinearRegression();
  const startTime = Date.now();

  for (let attempt = 0; attempt < MAX_UPLOAD_RETRIES; attempt++) {
    if (signal.aborted) throw new Error('Aborted');
    try {
      const result = await api.fs.uploadFile(
        file,
        job.parent_node_id,
        signal,
        (loaded, total) => {
          const { remainingTime, uploadSpeed } = linreg.updateRemainingTime(file.size - loaded);
          dispatch(
            actions.jobUpdated({
              jobID: job.uuid,
              jobProps: {
                uploadProgress: total > 0 ? loaded / total : 0,
                remainingTime,
                ellapsedTime: Date.now() - startTime,
                uploadSpeed,
              },
            }),
          );
        },
        true, // overwrite
        fontLicenseIds,
      );

      if (signal.aborted) throw new Error('Aborted');

      if (result.success) {
        return result.response.node.id as number;
      }

      const errorMessage = errorTextFromResponse(result.response, 'Upload failed');

      // License/validation errors are deterministic: retrying only delays the real
      // reason (font_license_required, invalid_jira_url, ...). Fail fast with a
      // clear message shown via the job's errorMessage.
      if (isNonRetryableUploadCode(result.response?.code)) {
        throw new NonRetryableUploadError(uploadErrorMessage(result.response?.code, errorMessage));
      }
      if (attempt + 1 < MAX_UPLOAD_RETRIES) {
        await delay(RETRY_DELAY_MS);
        continue;
      }
      throw new Error(errorMessage);
    } catch (e) {
      if (signal.aborted) throw e;
      // Deterministic rejection — rethrow at once so the reason reaches the user
      // instead of being retried 5 times behind a ~10s delay.
      if (e instanceof NonRetryableUploadError) throw e;
      if (attempt + 1 >= MAX_UPLOAD_RETRIES) throw e;
      await delay(RETRY_DELAY_MS);
    }
  }
  throw new Error('Upload failed after maximum retries');
}

async function uploadMultipart(
  dispatch: Dispatch,
  job: Job,
  file: File,
  signal: AbortSignal,
  fontLicenseIds?: number[],
): Promise<number> {
  // Pre-hash the whole file (streaming, slice-by-slice — no full-file ArrayBuffer)
  // for the new create_multipart_upload contract; the backend verifies md5_of_file
  // when the last part arrives.
  const md5OfFile = await computeFileMd5(file, CHUNK_SIZE);
  if (signal.aborted) throw new Error('Aborted');

  // Asset Lab always force-binds, so overwrite is always true here.
  const uploadIdResult = await api.fs.getUploadID(
    file,
    job.parent_node_id,
    CHUNK_SIZE,
    md5OfFile,
    true,
    fontLicenseIds,
  );
  if (signal.aborted) throw new Error('Aborted');
  if (!uploadIdResult.success) {
    // The license check runs on the init request too, so this is where a big archive
    // learns its licenses are missing/invalid. create_multipart_upload's 400 body
    // ({ code, data }) carries the real reason — surface it instead of a generic
    // "failed to initialize", otherwise a rejected license looks like a glitch.
    const response = uploadIdResult.errorData?.response;
    const fallback = errorTextFromResponse(response, 'Failed to initialize multipart upload');
    if (isNonRetryableUploadCode(response?.code)) {
      throw new NonRetryableUploadError(uploadErrorMessage(response?.code, fallback));
    }
    throw new Error(fallback);
  }

  const { id: uploadId } = uploadIdResult.data.response;

  const linreg = new TimeWindowedLinearRegression();
  const totalParts = Math.ceil(file.size / CHUNK_SIZE);
  const startTime = Date.now();
  let createdNodeId: number | null = null;

  for (let currentPart = 1; currentPart <= totalParts; currentPart++) {
    if (signal.aborted) throw new Error('Aborted');

    const partData = await getBlobPart(file, currentPart);

    const isLastPart = currentPart === totalParts;
    const currentlyUploadedBytes = CHUNK_SIZE * (currentPart - 1);

    let succeeded = false;
    for (let attempt = 0; attempt < MAX_UPLOAD_RETRIES; attempt++) {
      if (signal.aborted) throw new Error('Aborted');
      try {
        const result = await api.fs.uploadFilePart(
          currentPart,
          totalParts,
          partData,
          uploadId,
          signal,
          (loaded) => {
            const totalLoaded = currentlyUploadedBytes + loaded;
            const { remainingTime, uploadSpeed } = linreg.updateRemainingTime(
              file.size - totalLoaded,
            );
            dispatch(
              actions.jobUpdated({
                jobID: job.uuid,
                jobProps: {
                  uploadProgress: file.size > 0 ? totalLoaded / file.size : 0,
                  uploadSpeed,
                  remainingTime,
                  ellapsedTime: Date.now() - startTime,
                },
              }),
            );
          },
        );
        if (signal.aborted) throw new Error('Aborted');
        if (result?.success) {
          // The backend creates the file node only on the last part:
          //  - intermediate parts -> 202 (empty body, response === null)
          //  - last part          -> 201 with the node as a FLAT object in the
          //    response root (NodeSerializer(node).data + `perms`), NOT { node }.
          // `sendfile` doesn't distinguish 2xx statuses, so check explicitly.
          if (isLastPart) {
            if (result.status === 201 && result.response && result.response.id) {
              createdNodeId = result.response.id;
              succeeded = true;
              break;
            }
            // last part without a 201 node -> incomplete file, retry
          } else {
            succeeded = true;
            break;
          }
        }
        if (attempt + 1 < MAX_UPLOAD_RETRIES) {
          await delay(RETRY_DELAY_MS);
        }
      } catch (e) {
        if (signal.aborted) throw e;
        if (attempt + 1 >= MAX_UPLOAD_RETRIES) throw e;
        await delay(RETRY_DELAY_MS);
      }
    }
    if (!succeeded) throw new Error(`Failed to upload part ${currentPart}/${totalParts}`);
  }

  if (createdNodeId === null) {
    throw new Error('Multipart upload finished without a created node');
  }
  return createdNodeId;
}

async function getBlobPart(
  file: File,
  currentPart: number,
): Promise<{ md5: string; arrayBuffer: ArrayBuffer }> {
  const start = (currentPart - 1) * CHUNK_SIZE;
  const end = Math.min(start + CHUNK_SIZE, file.size);
  const arrayBuffer = await file.slice(start, end).arrayBuffer();
  const spark = new SparkMD5.ArrayBuffer();
  spark.append(arrayBuffer);
  return { md5: spark.end(), arrayBuffer };
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

class TimeWindowedLinearRegression {
  private records: [number, number, number][] = [];
  constructor(private window?: number) {}

  updateRemainingTime(remainingBytes: number) {
    const now = Date.now();
    this.append(remainingBytes, now);
    const { slope, yIntercept } = this.calc();
    return {
      remainingTime: yIntercept - now,
      uploadSpeed: slope !== 0 ? Math.abs(1000 / slope) : 0,
    };
  }

  private cleanup() {
    if (this.window === undefined) return;
    const now = Date.now();
    const index = this.records.findIndex((record) => now - record[2] < (this.window as number));
    this.records = index >= 0 ? this.records.slice(index) : [];
  }

  private append(x: number, y: number) {
    this.cleanup();
    this.records.push([x, y, Date.now()]);
  }

  private calc() {
    this.cleanup();
    const length = this.records.length;
    if (length === 0) return { slope: NaN, yIntercept: NaN };
    const sumX = sum(this.records.map((r) => r[0]));
    const sumY = sum(this.records.map((r) => r[1]));
    const meanX = sumX / length;
    const meanY = sumY / length;
    const dxdySum = sum(this.records.map(([x, y]) => (x - meanX) * (y - meanY)));
    const dxSqSum = sum(this.records.map(([x]) => (x - meanX) ** 2));
    const slope = dxdySum / dxSqSum;
    const yIntercept = meanY - slope * meanX;
    return { slope, yIntercept };
  }
}
